import { getDateAggregationKey } from "../../../utils/getDateAggregationKey";

interface BacktestRow {
    time: string;
    decision?: number | null;
    position?: number | null;
    fill_price?: number | null;
    market_price?: number | null;
}

interface Props {
    data: BacktestRow[];
}

function formatNumber(value: number | null | undefined, digits = 4): string {
    if (value == null || !Number.isFinite(value)) return "—";
    return value.toFixed(digits);
}

export function BacktestTableWidget({ data }: Props) {
    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <style>{`
                .backtest-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    width: 100%;
                }
                .backtest-table th {
                    padding: 0.75rem 1rem;
                    border-bottom: 2px solid var(--surface-glass-border);
                    background: var(--bg-panel);
                    text-align: left;
                    color: var(--text-muted);
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.7rem;
                    letter-spacing: 0.05em;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                .backtest-table td {
                    padding: 0.5rem 1rem;
                    border-bottom: 1px solid var(--surface-glass-border);
                    font-size: 0.875rem;
                }
                .backtest-table tr:hover td {
                    background-color: rgba(255, 255, 255, 0.03);
                }
                .backtest-table tr:last-child td {
                    border-bottom: none;
                }
            `}</style>
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                <table className="backtest-table">
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Decision</th>
                            <th>Position</th>
                            <th>Fill</th>
                            <th>Market</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.slice(0, 1000).map((row, i) => {
                            const decision = row.decision ?? 0;
                            const decisionColor = decision > 0 ? 'var(--accent-success)' : decision < 0 ? 'var(--accent-danger)' : 'var(--text-primary)';
                            
                            return (
                                <tr key={i}>
                                    <td style={{ color: 'var(--text-secondary)' }}>
                                        {getDateAggregationKey(row.time)}
                                    </td>
                                    <td style={{ color: decisionColor, fontWeight: decision !== 0 ? '600' : 'normal' }}>
                                        {formatNumber(row.decision, 0)}
                                    </td>
                                    <td style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                                        {formatNumber(row.position, 0)}
                                    </td>
                                    <td style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                                        {formatNumber(row.fill_price)}
                                    </td>
                                    <td style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                                        {formatNumber(row.market_price)}
                                    </td>
                                </tr>
                            );
                        })}
                        {data.length > 1000 && (
                            <tr>
                                <td colSpan={5} style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontStyle: "italic", fontSize: '0.8rem' }}>
                                    Showing first 1,000 of {data.length.toLocaleString()} rows. Use CSV export for full dataset.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
